//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { GoogleCatalogIngestionModalActions } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import CatalogUrlField from '@catalog-ingestion/components/connect-panel/CatalogUrlField';
import FileField from '@catalog-ingestion/components/connect-panel/FileField';
import RadioButtonField from '@catalog-ingestion/components/connect-panel/RadioButtonField';
import SelectLocaleField from '@catalog-ingestion/components/connect-panel/SelectLocaleField';
import ValidationPanel from '@catalog-ingestion/components/validation-panel/ValidationPanel';
import { CONNECTION_METHODS } from '@catalog-ingestion/constants/connectPanelConstants';
import { selectCatalogFailureMessage } from '@catalog-ingestion/utils/selectCatalogFailureMessage';
import { Alert } from '@components/atoms/alert/Alert';
import { ICuralateStore } from '@models/AppState';

import { ReduxDispatch } from 'app/curalate.store';

function mapStateToProps(state: ICuralateStore) {
  return {
    ...state.catalogIngestionReducers.connectPanel,
    currentPanel:
      state.catalogIngestionReducers.googleCatalogIngestionModal.currentPanel,
    catalogSource: state.catalogIngestionReducers.connectPanel.catalogSource,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch) {
  return {
    actions: {
      modal: bindActionCreators(GoogleCatalogIngestionModalActions, dispatch),
    },
  };
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

export const ConnectPanel = (props: Props) => {
  const {
    isValidating,
    isCatalogSourceInvalid,
    catalogSource,
    isUrlEmpty,
    actions: {
      modal: { scheduleValidation },
    },
  } = props;

  function displayConnectionMethod() {
    if (props.connectionMethod === CONNECTION_METHODS.CatalogURL) {
      return <CatalogUrlField />;
    } else {
      return <FileField />;
    }
  }

  function displayEmptyUrlError() {
    return (
      <Alert type='error' className='cu-mb-3'>
        {props.connectionMethod === CONNECTION_METHODS.CatalogURL
          ? 'You must enter a catalog URL in order to continue to the next step'
          : 'You must upload a file in order to continue to the next step'}
      </Alert>
    );
  }

  const [isSchedulingValidation, setIsSchedulingValidation] = useState(true);

  if (isValidating) {
    if (isSchedulingValidation) {
      scheduleValidation();
      setIsSchedulingValidation(false);
    }
    return <ValidationPanel />;
  } else {
    return (
      <>
        {isUrlEmpty && displayEmptyUrlError()}
        {isCatalogSourceInvalid && (
          <Alert type='error' className='cu-mb-3'>
            {selectCatalogFailureMessage(catalogSource.failureReason)}
          </Alert>
        )}
        <Sheet>
          <div className='cu-text-subtitle'>Connect Your Product Catalog</div>
          <SheetText className='cu-text-body'>
            Review Google's{' '}
            <a
              className='cu-text-link-primary'
              target='_blank'
              href='https://support.google.com/merchants/answer/7052112'
              rel='noreferrer'
            >
              product feed specifications
            </a>{' '}
            to ensure your catalog adheres to requirements. Connect your catalog
            to check for errors before ingesting your catalog. Need to set up a
            product feed?{' '}
            <a
              className='cu-text-link-primary'
              target='_blank'
              href='http://developers.curalate.com/guide/product-catalogs/'
              rel='noreferrer'
            >
              Follow this step-by-step guide
            </a>
          </SheetText>
          {<RadioButtonField />}
          {<HorizontalDivider />}
          {displayConnectionMethod()}
          <SelectLocaleField />
        </Sheet>
      </>
    );
  }
};

const Sheet = styled.div.attrs({ className: 'cu-sheet-low' })`
  padding: ${({ theme }) => theme.spacings[4]};
  margin-top: ${({ theme }) => theme.spacings[3]};
`;

const SheetText = styled.div`
  padding: ${({ theme }) => theme.spacings[2]} 0px;
`;

const HorizontalDivider = styled.hr`
  height: 1px;
  width: 100%;
  color: ${({ theme }) => theme.colors.gray7};
`;

export default connect(mapStateToProps, mapDispatchToProps)(ConnectPanel);
